import { createApi } from '@reduxjs/toolkit/query/react';
import type { Message } from '@copilotkit/shared';
import { RootState } from '@/redux/store';
import { baseQueryCamelized } from '@/redux/slices/helpers/baseQueryCamelized';
import {
  AiConversation,
  FeedbackReason,
  MessageFeedbackEntry,
} from '@/types/aiChat';
import { TagTypes } from '@/redux/slices/apiSlice';
import type {
  CreateScheduledTaskRequest,
  ScheduledTask,
  UpdateScheduledTaskRequest,
} from '@/types/scheduledTasks';
import {
  setFeedback,
  MessageFeedbackState,
} from '@/redux/slices/messageFeedbackSlice';

type UpdateMessageFeedbackRequest = {
  organizationId: string;
  conversationId: string;
  messageId: string;
  rating: MessageFeedbackEntry['rating'];
  reasons: FeedbackReason[];
};

// TODO: This should be generated by the backend library
export type UpdateMessageFeedbackResponseDto = {
  message_id: string;
  rating: MessageFeedbackEntry['rating'];
  reasons: FeedbackReason[];
  feedback_updated_at: string;
};

// TODO: This should be generated by the backend library
export type AiConversationDto = {
  conversation_id: string;
  user_id: string;
  organization_id: string;
  created_at: string;
  expires_at: string;
  updated_at?: string;
};

// Tasks are created as the caller's own...
const ownTasksUrl = (organizationId: string) =>
  `organizations/${organizationId}/users/me/scheduled-tasks`;
// ...but admins list, edit and delete every task in the organization, whoever owns it.
const organizationTasksUrl = (organizationId: string) =>
  `organizations/${organizationId}/scheduled-tasks`;

// Not built with invalidatesList(): that helper widens `type` to the whole
// TagTypes enum, which this slice's narrower tagTypes list rejects.
const scheduledTaskListTag = [
  { type: TagTypes.ScheduledTask, id: 'LIST' },
] as const;

// Tracks the requestId of the most recently started updateMessageFeedback
// call per messageId, so that if a slower request's rollback fires after a
// newer request for the same message has already committed, the rollback is
// skipped instead of clobbering the newer, already-persisted state.
const latestFeedbackRequestId: Record<string, string> = {};

// Extracted (rather than inlined in the endpoint config) so it can be unit
// tested directly with hand-built dispatch/getState/queryFulfilled stand-ins,
// without going through fetchBaseQuery's real network stack.
export const updateMessageFeedbackOnQueryStarted = async (
  {
    messageId,
    rating,
    reasons,
  }: Pick<UpdateMessageFeedbackRequest, 'messageId' | 'rating' | 'reasons'>,
  {
    dispatch,
    getState,
    queryFulfilled,
    requestId,
  }: {
    dispatch: (action: ReturnType<typeof setFeedback>) => unknown;
    // Typed loosely (RTK Query's own MutationLifecycleApi.getState is scoped
    // to just this api slice's own reducerPath, not the full combined app
    // store) and cast below — matching how this cross-slice read worked
    // before extraction, via `getState() as RootState`. Also avoids a
    // circular type reference: RootState (in store.ts) depends on this
    // file's exported gigawattBackendApiSlice, which would depend back on
    // RootState if this exported function's signature used it directly.
    getState: () => unknown;
    queryFulfilled: Promise<unknown>;
    requestId: string;
  }
) => {
  const state = getState() as { messageFeedback: MessageFeedbackState };
  const previousFeedback = state.messageFeedback.feedback[messageId] ?? {
    rating: null,
    reasons: [],
  };

  latestFeedbackRequestId[messageId] = requestId;
  dispatch(setFeedback({ messageId, rating, reasons }));

  try {
    await queryFulfilled;
  } catch {
    // Only roll back if no newer request for this message has started
    // since — otherwise this stale failure would overwrite a rating that
    // was already successfully committed later.
    if (latestFeedbackRequestId[messageId] === requestId) {
      dispatch(setFeedback({ messageId, ...previousFeedback }));
    }
  }
};

const transformAiConversation = (
  conversationDto: AiConversationDto
): AiConversation => {
  return {
    conversationId: conversationDto.conversation_id,
    userId: conversationDto.user_id,
    organizationId: conversationDto.organization_id,
    createdAt: conversationDto.created_at,
    expiresAt: conversationDto.expires_at,
    updatedAt: conversationDto.updated_at,
  };
};

export const gigawattBackendApiSlice = createApi({
  reducerPath: 'gigawattBackendApiSlice',
  baseQuery: baseQueryCamelized(
    {
      baseUrl: window._env_?.VITE_GIGAWATT_URL,
      prepareHeaders: (headers, api) => {
        const accessToken = (api.getState() as RootState).loggedUser
          ?.accessToken;

        if (accessToken) {
          headers.set('authorization', `Bearer ${accessToken}`);
        }

        return headers;
      },
    },
    { shouldCamelizeResponse: false }
  ),
  endpoints: builder => ({
    newConversation: builder.mutation<AiConversation, string>({
      query: organizationId => ({
        url: `organizations/${organizationId}/users/me/conversations`,
        method: 'POST',
      }),
      transformResponse: transformAiConversation,
      invalidatesTags: [TagTypes.AiConversation],
    }),

    getConversationMessages: builder.query<
      Message[],
      { organizationId: string; conversationId: string }
    >({
      query: ({ organizationId, conversationId }) => ({
        url: `organizations/${organizationId}/users/me/conversations/${conversationId}`,
        method: 'GET',
      }),
    }),

    updateMessageFeedback: builder.mutation<
      MessageFeedbackEntry,
      UpdateMessageFeedbackRequest
    >({
      query: ({
        organizationId,
        conversationId,
        messageId,
        rating,
        reasons,
      }) => ({
        url: `organizations/${organizationId}/users/me/conversations/${conversationId}/messages/${messageId}/feedback`,
        method: 'PUT',
        body: { rating, reasons },
      }),
      transformResponse: (
        response: UpdateMessageFeedbackResponseDto
      ): MessageFeedbackEntry => ({
        rating: response.rating,
        reasons: response.reasons,
      }),
      onQueryStarted: updateMessageFeedbackOnQueryStarted,
    }),

    // The list provides and every mutation invalidates the ScheduledTask LIST tag.
    getOrganizationScheduledTasks: builder.query<ScheduledTask[], string>({
      query: organizationId => ({
        url: organizationTasksUrl(organizationId),
        method: 'GET',
      }),
      providesTags: () => scheduledTaskListTag,
    }),

    createScheduledTask: builder.mutation<
      ScheduledTask,
      { organizationId: string; body: CreateScheduledTaskRequest }
    >({
      query: ({ organizationId, body }) => ({
        url: ownTasksUrl(organizationId),
        method: 'POST',
        body,
      }),
      invalidatesTags: () => scheduledTaskListTag,
    }),

    updateScheduledTask: builder.mutation<
      ScheduledTask,
      {
        organizationId: string;
        scheduleId: string;
        body: UpdateScheduledTaskRequest;
      }
    >({
      query: ({ organizationId, scheduleId, body }) => ({
        url: `${organizationTasksUrl(organizationId)}/${scheduleId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: () => scheduledTaskListTag,
    }),

    deleteScheduledTask: builder.mutation<
      void,
      { organizationId: string; scheduleId: string }
    >({
      query: ({ organizationId, scheduleId }) => ({
        url: `${organizationTasksUrl(organizationId)}/${scheduleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => scheduledTaskListTag,
    }),
  }),
  tagTypes: [TagTypes.AiConversation, TagTypes.ScheduledTask],
});

// export auto-generated hooks
export const {
  useNewConversationMutation,
  useLazyGetConversationMessagesQuery,
  useUpdateMessageFeedbackMutation,
  useGetOrganizationScheduledTasksQuery,
  useCreateScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
  useDeleteScheduledTaskMutation,
} = gigawattBackendApiSlice;
